import { JsonObject } from '@/lib/helpers';
import { BaseModel } from '@/lib/models/base.model';
import { object, string } from 'yup';
import { PolicyAccessList, PolicyMeta, PolicyPriority, PolicyScope } from '.';
import { PublicServiceConfig } from './public-services';
import { ContentControlConfig } from './content-controls';
import { SensitiveDataConfig } from './sensitive-data';
import { MetadataControlConfig } from './metadata-controls';
import { ClassifierControlConfig, RegexPatternConfig } from './classifier-controls';
import { McpControlConfig } from './mcp-controls';
import { AssistantConfig, DatasourceConfig, PortalConfig } from './portal';
import { RerouteConfig } from './reroute';

export class PolicyModel extends BaseModel {
  // core props
  orgId: string;
  name: string;
  description: string;
  priority: number;

  // legacy v1 prop, superseded by priority
  scope: PolicyScope;

  // targeting
  publicServices?: PublicServiceConfig;
  groups?: string[];
  allGroups?: boolean;

  // portal
  portal?: PortalConfig;
  contextDataSources?: DatasourceConfig;
  assistants?: AssistantConfig;

  // sensitive data
  sensitiveData?: SensitiveDataConfig;
  contentControls?: ContentControlConfig;

  // mcp
  mcpControls?: McpControlConfig;

  // service controls
  metadataControls?: MetadataControlConfig;

  // intents
  intents?: PolicyAccessList;
  classifierControls?: ClassifierControlConfig;
  regexPatterns?: RegexPatternConfig;

  // prop for IP detection demo
  sensitiveDataSources?: string[];

  reroute?: RerouteConfig;

  schema = object({
    orgId: string().required('Organization is required'),
    name: string().required('Name is required'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof PolicyModel, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.name = (data.name as string) || '';
    this.description = (data.description as string) || '';
    this.priority = (data.priority as number) || 9999;

    // @todo REMOVE once policy service starts returning priority
    this.scope = (data.scope as PolicyScope) || 'scoped';

    if (data.publicServices) {
      this.publicServices = new PublicServiceConfig(data.publicServices as JsonObject);
    }

    if (data.groups) {
      this.groups = data.groups as string[];
    }

    this.allGroups = !!data.allGroups;

    if (data.portal) {
      this.portal = new PortalConfig(data.portal as JsonObject);
    }

    if (data.contextDataSources) {
      this.contextDataSources = new DatasourceConfig(data.contextDataSources as JsonObject);
    }

    if (data.assistants) {
      this.assistants = new AssistantConfig(data.assistants as JsonObject);
    }

    if (data.sensitiveData) {
      this.sensitiveData = new SensitiveDataConfig(data.sensitiveData as JsonObject);
    }

    if (data.contentControls) {
      this.contentControls = new ContentControlConfig(data.contentControls as JsonObject);
    }

    if (data.mcpControls) {
      this.mcpControls = new McpControlConfig(data.mcpControls as JsonObject);
    }

    if (data.metadataControls) {
      this.metadataControls = new MetadataControlConfig(data.metadataControls as JsonObject);
    }

    if (data.intents) {
      this.intents = data.intents as PolicyAccessList;
    }

    if (data.classifierControls) {
      this.classifierControls = new ClassifierControlConfig(data.classifierControls as JsonObject);
    }

    if (data.regexPatterns) {
      this.regexPatterns = new RegexPatternConfig(data.regexPatterns as JsonObject);
    }

    this.sensitiveDataSources = (data.sensitiveDataSources as string[]) || [];

    if (data.reroute) {
      this.reroute = new RerouteConfig(data.reroute as JsonObject);
    }
  }

  get meta(): PolicyMeta {
    return {
      id: this.id || '',
      name: this.name,
      description: this.description,
      scope: this.scope,
      groups: this.groups || [],
      publicServices: this.publicServices || new PublicServiceConfig(),
      contextDataSources: this.contextDataSources || new DatasourceConfig(),
      portal: this.portal || new PortalConfig(),
      assistants: this.assistants || new AssistantConfig(),
      isDefault: this.isDefault,
    };
  }

  entityEnabled(type: string): boolean {
    return !!this.sensitiveData?.entities?.some(({ type: enabledType }) => enabledType === type);
  }

  get targetsPublicServices(): boolean {
    return !!this.publicServices?.serviceAccess?.length;
  }

  get newPublicServices(): string[] {
    const { serviceAccess = [] } = this.publicServices || { serviceAccess: [] };
    const protectedServices = serviceAccess
      .filter(({ action }) => action === 'protected')
      .map(({ serviceId }) => serviceId);

    return serviceAccess
      .filter(
        ({ serviceId, action }) => action === 'monitored' && !protectedServices.includes(serviceId)
      )
      .map(({ serviceId }) => serviceId);
  }

  get allServices(): boolean {
    return !!this.publicServices?.allServicesAction;
  }

  get isDefault(): boolean {
    // @todo REMOVE once policy service starts returning priority
    return this.scope === 'org';
    // return this.priority === (PolicyPriority.Default as number);
  }

  get isOverride(): boolean {
    return this.priority === (PolicyPriority.Override as number);
  }

  get niceName(): string {
    if (this.isDefault) {
      return 'Default Policy';
    }

    if (this.isOverride) {
      return 'Override Policy';
    }

    return this.name;
  }
}
